import {
	axiosErrorHandler,
	get,
	post,
	put,
	deletes,
} from 'models/shared/helpers/axiosInstance';
import { buildPathWithQueryParams } from 'models/shared/helpers/utils';
import {
	User,
	CreateUserDto,
	UpdateUserDto,
	UserFilterDto,
	Role,
} from '../interfaces/user-management.interface';

const baseUserPath = '/v1/user';
const baseRolePath = '/v1/user/role';

export const fetchUsers = async (dto?: UserFilterDto) => {
	try {
		const url = dto
			? buildPathWithQueryParams(baseUserPath, dto)
			: baseUserPath;
		const res = await get<{
			count: number;
			pages: number;
			page: number;
			data: User[];
		}>(url);
		return Array.isArray(res.data) ? res.data : res.data.data || [];
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchUser = async (id: string) => {
	try {
		const userId = String(id).trim();
		const url = `${baseUserPath}/${encodeURIComponent(userId)}`;
		const res = await get<User>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const createUser = async (dto: CreateUserDto, imageFile?: File) => {
	try {
		const formData = new FormData();
		Object.keys(dto).forEach((key) => {
			const value = dto[key as keyof CreateUserDto];
			if (value !== undefined) {
				if (Array.isArray(value)) {
					value.forEach((item) => formData.append(key, item));
				} else {
					formData.append(key, value as string);
				}
			}
		});
		if (imageFile) {
			formData.append('imageFile', imageFile);
		}

		const res = await post<User>(baseUserPath, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const updateUser = async (
	id: string,
	dto: UpdateUserDto,
	imageFile?: File,
) => {
	try {
		const userId = String(id).trim();
		if (!userId) {
			throw new Error('User ID is required');
		}

		const formData = new FormData();
		Object.keys(dto).forEach((key) => {
			const value = dto[key as keyof UpdateUserDto];
			if (value !== undefined) {
				if (Array.isArray(value)) {
					value.forEach((item) => formData.append(key, item));
				} else if (typeof value === 'boolean') {
					formData.append(key, value.toString());
				} else {
					formData.append(key, value as string);
				}
			}
		});
		if (imageFile) {
			formData.append('imageFile', imageFile);
		}

		const url = `${baseUserPath}/${encodeURIComponent(userId)}`;
		const res = await put<User>(url, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const deleteUser = async (id: string) => {
	try {
		const userId = String(id).trim();
		const url = `${baseUserPath}/${encodeURIComponent(userId)}`;
		await deletes(url);
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchCompanyVehicles = async (companyId: string) => {
	try {
		const url = `${baseUserPath}/company/${encodeURIComponent(
			companyId,
		)}/vehicles`;
		const res = await get<
			Array<{
				_id: string;
				internalId: string;
				description: string;
				status?: string;
			}>
		>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchRoles = async () => {
	try {
		const res = await get<{
			count: number;
			pages: number;
			page: number;
			data: Role[];
		}>(baseRolePath);
		return Array.isArray(res.data) ? res.data : res.data.data || [];
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const resetUserPassword = async (id: string) => {
	try {
		const userId = String(id).trim();
		const url = `${baseUserPath}/${encodeURIComponent(userId)}/reset-password`;
		const res = await post<{ message: string }>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
